import {createFileRoute, Link, Outlet, redirect} from '@tanstack/react-router';
import {useState} from 'react';
import {Navbar} from '@/components/ui/navbar';
import {Typography} from '@/components/ui/typography';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {Button} from '@/components/ui/button';
import {FileText, Menu, Plus, X} from 'lucide-react';

export const Route = createFileRoute('/admin/blog')({
  component: AdminBlogLayout,
  beforeLoad: ({context, location}) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: {redirect: location.href},
      });
    }
    if (context.profile.level !== 'superadmin') {
      throw redirect({to: '/'});
    }
  },
});

const navItemClass =
  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors';

/** Sidebar navigation links shared between desktop and mobile drawer. */
function SidebarNav({onNavigate}: {onNavigate?: () => void}) {
  return (
    <nav className="space-y-1">
      <Link
        to="/admin/blog"
        activeOptions={{exact: true}}
        onClick={onNavigate}
        className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
        activeProps={{
          className: `${navItemClass} bg-blue-50 text-blue-700`,
        }}
      >
        <FileText className="w-4 h-4" />
        All Posts
      </Link>
      <Link
        to="/admin/blog-create"
        onClick={onNavigate}
        className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
        activeProps={{
          className: `${navItemClass} bg-blue-50 text-blue-700`,
        }}
      >
        <Plus className="w-4 h-4" />
        Create Post
      </Link>
    </nav>
  );
}

/** Dashboard layout with sidebar navigation and routed content via Outlet. */
function AdminBlogLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      <div className="bg-white border-b border-neutral-200 sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center gap-3">
          <Drawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            direction="left"
          >
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm" className="lg:hidden -ml-2">
                <Menu className="w-5 h-5" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="flex flex-row items-center justify-between">
                <DrawerTitle>Blog</DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="sm">
                    <X className="w-5 h-5" />
                  </Button>
                </DrawerClose>
              </DrawerHeader>
              <div className="px-4 pb-4">
                <SidebarNav onNavigate={() => setDrawerOpen(false)} />
              </div>
            </DrawerContent>
          </Drawer>

          <div className="min-w-0">
            <Typography level="H4" className="truncate">
              Blog
            </Typography>
            <Typography level="C2" className="text-gray-500">
              Manage blog posts
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-1 w-full mx-auto min-w-0">
        {/* Desktop Dashboard Body */}
        <div className="flex gap-6 flex-1 min-w-0">
          <div className="hidden lg:block w-56 flex-shrink-0 bg-white p-6 border-r border-neutral-100">
            <SidebarNav />
          </div>

          <div className="flex-1 min-w-0 p-6 flex flex-col">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
